import { ALLERGY_BASE_URL } from "@qendo/medical";
import { AllergyLevel } from "@qendo/medical/allergy";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Allergy } from "~/entities/medical/allergy";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

export interface AllergyListingItem {
    id: string;
    title: string;
    emergency: boolean;
    level: AllergyLevel;
}

const type = MedicalType.allergy;

export default function AllergiesIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: AllergyListingItem[] = useMemo(() => {
        return listMedical(type).map((a: Allergy) => ({
            id: a.id as string,
            title: findOption(OptionType.allergies, a.option_id)?.title as string,
            emergency: a.flag,
            level: a.level,
        }));
    }, [listMedical, findOption]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="allergies">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share allergies" initial={["allergies", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Allergies"
                    icon="denied"
                    button={() => (
                        <Can ability="update" section="allergies">
                            <Button
                                title="Add allergy"
                                variant="primary"
                                size="lg"
                                href="/health-profile/allergies/create"
                            />
                        </Can>
                    )}
                >
                    Document known and suspected allergies to medications, foods, or other substances.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<AllergyListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        groups={[
                            { name: "Known", matches: (item) => item.level === AllergyLevel.known },
                            { name: "Suspected", matches: (item) => item.level === AllergyLevel.suspected },
                            { name: "Sensitivity", matches: (item) => item.level === AllergyLevel.sensitivity },
                        ]}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                badge={item.emergency ? "Emergency reaction" : null}
                                badgeVariant="danger"
                                href={{ pathname: `${ALLERGY_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
